import { heTimesheetsService } from "@/apis/services"
import { IAttachmentUrlDto, IHeTimesheet, IHeTimesheetDto, IHeTimesheetSpvParams, IHeTimesheetUpdateDto, IPaginationMeta, IPaginationParam } from "@/types"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

const getErrorMessage = (error: unknown): string => {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
  if (apiMessage) return apiMessage
  if (error instanceof Error) return error.message
  return "An unknown error occurred"
}

interface IHeTimesheetState {
  heTimesheets: IHeTimesheet[]
  currentHeTimesheet: IHeTimesheet | null
  meta?: IPaginationMeta
  isLoading: boolean
  error: string | null
  createHeTimesheet: (dto: IHeTimesheetDto) => Promise<void>
  fetchHeTimesheets: (params?: IPaginationParam) => Promise<void>
  fetchHeTimesheetsSupervisor: (params?: IHeTimesheetSpvParams) => Promise<void>
  fetchHeTimesheetById: (id: string) => Promise<void>
  fetchHeTimesheetAttachmentUrl: (dto: IAttachmentUrlDto) => Promise<string>
  updateHeTimesheet: (dto: IHeTimesheetUpdateDto, recordId: string) => Promise<void>
}

const initialState = {
  heTimesheets: [],
  currentHeTimesheet: null,
  meta: undefined,
  isLoading: false,
  error: null,
}

export const useHeTimesheetStore = create<IHeTimesheetState>()(
  devtools(set => ({
    ...initialState,

    createHeTimesheet: async (dto: IHeTimesheetDto) => {
      set({ isLoading: true, error: null })
      try {
        await heTimesheetsService.createHeTimesheet(dto)
      } catch (error) {
        set({ error: getErrorMessage(error) })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchHeTimesheets: async (params?: IPaginationParam) => {
      set({ isLoading: true, error: null })
      try {
        const response = await heTimesheetsService.getHeTimesheets(params)
        set({ heTimesheets: response.data.items, meta: response.data.meta })
      } catch (error) {
        set({ error: getErrorMessage(error) })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchHeTimesheetsSupervisor: async (params?: IHeTimesheetSpvParams) => {
      set({ isLoading: true, error: null })
      try {
        const response = await heTimesheetsService.getHeTimesheetsSupervisor(params)
        set({ heTimesheets: response.data.items, meta: response.data.meta })
      } catch (error) {
        set({ error: getErrorMessage(error) })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchHeTimesheetById: async (id: string) => {
      set({ isLoading: true, error: null })
      try {
        const response = await heTimesheetsService.getHeTimesheetById(id)
        set({ currentHeTimesheet: response.data })
      } catch (error) {
        set({ error: getErrorMessage(error) })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchHeTimesheetAttachmentUrl: async (dto: IAttachmentUrlDto) => {
      try {
        const response = await heTimesheetsService.getHeTimesheetAttachmentUrl(dto)
        return response.data.url
      } catch (error) {
        set({ error: getErrorMessage(error) })
        throw error
      }
    },

    updateHeTimesheet: async (dto: IHeTimesheetUpdateDto, recordId: string) => {
       set({ isLoading: true, error: null })
      try {
        await heTimesheetsService.updateHeTimesheet(dto, recordId)
      } catch (error) {
        set({ error: getErrorMessage(error) })
        throw error
      } finally {
        set({ isLoading: false })
      }
    }
  }))
)
